import { useEffect, useRef, useState } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import { isRequestAbortError } from "../api/deals";
import {
  fetchViewingsLinkedToDeal,
  fetchViewingDealLinkCandidates,
  linkViewingToDeal,
  unlinkViewingFromDeal,
  unwrapViewingListPayload,
} from "../api/dealViewings";
import DealWorkspaceSectionHeader from "./DealWorkspaceSectionHeader";

/**
 * D2-D1 — private Viewings section for a Deal party.
 * Lists only CRM-readable Viewings linked to this Deal for the current actor.
 * Manual link/unlink only; never discloses counterparty Viewing existence.
 *
 * D2-D1-R1 — after successful link/unlink, authoritatively refresh both
 * linked rows and candidates as part of the mutation success flow.
 */

const STATUS_LABELS = {
  scheduled: "Заплановано",
  completed: "Завершено",
  cancelled: "Скасовано",
};

const formatDateTimeUk = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const agentLabel = (agent) => {
  if (!agent || typeof agent !== "object") return null;
  if (typeof agent.fullname === "string" && agent.fullname.trim()) {
    return agent.fullname.trim();
  }
  if (typeof agent.handle === "string" && agent.handle.trim()) {
    return agent.handle.trim();
  }
  return null;
};

function DealLinkViewingPicker({
  open,
  dealId,
  candidates,
  loadState,
  loadError,
  onRetry,
  onClose,
  onLink,
  submitting,
  formError,
  fieldErrors,
}) {
  if (!open) return null;

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-link-viewing-modal-title"
      data-testid="deal-link-viewing-picker"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={submitting ? undefined : onClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="deal-link-viewing-modal-title"
            className="feature-client-detail__modal-title"
          >
            Прив&apos;язати показ
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          {formError ? (
            <InlineError>{formError}</InlineError>
          ) : null}
          {fieldErrors.deal_id ? (
            <InlineError>{fieldErrors.deal_id}</InlineError>
          ) : null}

          {loadState === "loading" ? (
            <Spinner size="md" label="Завантаження кандидатів" />
          ) : null}

          {loadState === "error" ? (
            <div role="alert">
              <p>{loadError || "Не вдалося завантажити кандидатів."}</p>
              <button
                type="button"
                className="action-btn"
                onClick={onRetry}
              >
                Спробувати ще
              </button>
            </div>
          ) : null}

          {loadState === "loaded" && candidates.length === 0 ? (
            <EmptyState
              title="Немає кандидатів"
              description="Немає доступних показів для прив'язки до цієї угоди. Кандидати стосуються того самого об'єкта, що й угода."
            />
          ) : null}

          {loadState === "loaded" && candidates.length > 0 ? (
            <ul
              className="feature-deal-detail__timeline"
              data-testid="deal-viewing-candidates-list"
            >
              {candidates.map((row) => (
                <li
                  key={row.id}
                  className="feature-deal-detail__timeline-item"
                  data-testid={`deal-viewing-candidate-${row.id}`}
                >
                  <div className="feature-deal-detail__timeline-title">
                    {row.client_name || "Клієнт"}
                  </div>
                  <div className="feature-deal-detail__timeline-meta">
                    <span>
                      {STATUS_LABELS[row.status] || row.status || "—"}
                    </span>
                    {row.scheduled_at ? (
                      <span>
                        · <time dateTime={row.scheduled_at}>
                          {formatDateTimeUk(row.scheduled_at)}
                        </time>
                      </span>
                    ) : null}
                    {agentLabel(row.agent) ? (
                      <span>· {agentLabel(row.agent)}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="action-btn"
                    disabled={submitting || row.already_linked === true}
                    onClick={() => onLink(row.id)}
                    data-testid={`deal-link-viewing-${row.id}`}
                  >
                    {row.already_linked === true
                      ? "Уже прив'язано"
                      : "Прив'язати"}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DealPrivateViewingsSection({ dealId }) {
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [retryToken, setRetryToken] = useState(0);

  const [candidates, setCandidates] = useState([]);
  const [candLoadState, setCandLoadState] = useState("idle");
  const [candLoadError, setCandLoadError] = useState(null);
  const [candRetryToken, setCandRetryToken] = useState(0);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [unlinkState, setUnlinkState] = useState("idle");
  const [unlinkConfirmId, setUnlinkConfirmId] = useState(null);
  const [linkSubmitState, setLinkSubmitState] = useState("idle");
  const [linkFormError, setLinkFormError] = useState(null);
  const [linkFieldErrors, setLinkFieldErrors] = useState({});

  const unlinkingRef = useRef(false);
  const linkingRef = useRef(false);
  const mutationGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  const activeDealIdRef = useRef(dealId);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      mutationGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    activeDealIdRef.current = dealId;
    mutationGenerationRef.current += 1;
    setUnlinkConfirmId(null);
  }, [dealId]);

  const isStale = (generation) => (
    !mountedRef.current
    || generation !== mutationGenerationRef.current
    || activeDealIdRef.current !== dealId
  );

  // Initial / retry linked rows.
  useEffect(() => {
    if (dealId == null || dealId === "") {
      setLoadState("idle");
      setViewings([]);
      return undefined;
    }

    const controller = new AbortController();
    const generation = mutationGenerationRef.current;
    setLoadState("loading");
    setLoadError(null);
    setUnlinkState("idle");

    fetchViewingsLinkedToDeal(dealId, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted || isStale(generation)) return;
        setViewings(unwrapViewingListPayload(data));
        setLoadState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (isStale(generation)) return;
        setViewings([]);
        setLoadError(messageFromError(error));
        setLoadState("error");
      });

    return () => controller.abort();
    // isStale closes over dealId/generation intentionally via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, retryToken]);

  // Candidates when picker is open (or after candRetryToken bump while open).
  useEffect(() => {
    if (!pickerOpen || dealId == null || dealId === "") {
      if (!pickerOpen) {
        setCandLoadState("idle");
        setCandLoadError(null);
      }
      return undefined;
    }

    const controller = new AbortController();
    const generation = mutationGenerationRef.current;
    setCandLoadState("loading");
    setCandLoadError(null);

    fetchViewingDealLinkCandidates(dealId, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted || isStale(generation)) return;
        setCandidates(unwrapViewingListPayload(data));
        setCandLoadState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (isStale(generation)) return;
        setCandidates([]);
        setCandLoadError(messageFromError(error));
        setCandLoadState("error");
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, dealId, candRetryToken]);

  const refreshBothAuthoritative = async (generation) => {
    const [linkedData, candData] = await Promise.all([
      fetchViewingsLinkedToDeal(dealId),
      fetchViewingDealLinkCandidates(dealId),
    ]);
    if (isStale(generation)) return false;
    setViewings(unwrapViewingListPayload(linkedData));
    setCandidates(unwrapViewingListPayload(candData));
    setLoadState("loaded");
    setLoadError(null);
    setUnlinkState("idle");
    setCandLoadState("loaded");
    setCandLoadError(null);
    return true;
  };

  const handleRetry = () => {
    setUnlinkState("idle");
    setLoadError(null);
    setRetryToken((token) => token + 1);
  };

  const handleCandRetry = () => {
    setCandRetryToken((token) => token + 1);
  };

  const handleLink = async (viewingId) => {
    if (linkingRef.current) return;
    linkingRef.current = true;
    setLinkSubmitState("submitting");
    setLinkFormError(null);
    setLinkFieldErrors({});
    const generation = mutationGenerationRef.current;

    try {
      await linkViewingToDeal(viewingId, dealId);
      if (isStale(generation)) return;
      const refreshed = await refreshBothAuthoritative(generation);
      if (!refreshed) return;
      setLinkSubmitState("idle");
      linkingRef.current = false;
      setPickerOpen(false);
    } catch (error) {
      if (isStale(generation)) return;
      const parsed = parseDrfFieldErrors(error, ["deal_id"]);
      setLinkFieldErrors(parsed);
      setLinkFormError(parsed._form || messageFromError(error));
      setLinkSubmitState("error");
      linkingRef.current = false;
    }
  };

  const handleUnlinkRequest = (viewingId) => {
    if (unlinkingRef.current) return;
    setUnlinkConfirmId(viewingId);
  };

  const handleCancelUnlink = () => {
    if (unlinkState === "submitting") return;
    setUnlinkConfirmId(null);
  };

  const handleConfirmUnlink = async () => {
    const viewingId = unlinkConfirmId;
    if (viewingId == null || unlinkingRef.current) return;

    unlinkingRef.current = true;
    setUnlinkConfirmId(null);
    setUnlinkState("submitting");
    setLoadError(null);
    const generation = mutationGenerationRef.current;

    try {
      await unlinkViewingFromDeal(viewingId);
      if (isStale(generation)) return;
      const refreshed = await refreshBothAuthoritative(generation);
      if (!refreshed) return;
      setUnlinkState("idle");
      unlinkingRef.current = false;
    } catch (error) {
      if (isStale(generation)) return;
      setUnlinkState("error");
      unlinkingRef.current = false;
      setLoadError(messageFromError(error));
    }
  };

  const linking = linkSubmitState === "submitting";

  return (
    <section
      className="feature-deal-detail__section"
      aria-labelledby="deal-viewings-heading"
      data-testid="deal-private-viewings-section"
    >
      <DealWorkspaceSectionHeader
        id="deal-viewings-heading"
        eyebrow="Робота з об’єктом"
        title="Покази об’єкта"
        helper="Кандидати стосуються того самого об'єкта, що й угода. Покази бачите лише ви."
        iconClassName="fa-solid fa-calendar-day"
        actions={(
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              setLinkFormError(null);
              setLinkFieldErrors({});
              setLinkSubmitState("idle");
              linkingRef.current = false;
              setPickerOpen(true);
            }}
            data-testid="deal-link-viewing-button"
          >
            Прив&apos;язати показ
          </button>
        )}
      />

      {loadState === "loading" ? (
        <Spinner size="md" label="Завантаження показів об'єкта" />
      ) : null}

      {loadState === "error" || unlinkState === "error" ? (
        <div role="alert">
          <p>{loadError || "Не вдалося завантажити покази об'єкта."}</p>
          <button type="button" className="action-btn" onClick={handleRetry}>
            Спробувати ще
          </button>
        </div>
      ) : null}

      {loadState === "loaded" && viewings.length === 0 ? (
        <EmptyState
          className="feature-deal-detail__empty-compact"
          size="sm"
          icon="fa-solid fa-calendar-day"
          title="Немає показів об'єкта"
          description="У вас ще немає прив'язаних показів для цієї угоди."
        />
      ) : null}

      {loadState === "loaded" && viewings.length > 0 ? (
        <ul
          className="feature-deal-detail__timeline"
          data-testid="deal-private-viewings-list"
        >
          {viewings.map((viewing) => (
            <li
              key={viewing.id}
              className="feature-deal-detail__timeline-item"
              data-testid={`deal-viewing-row-${viewing.id}`}
            >
              <div className="feature-deal-detail__timeline-title">
                {viewing.client_name || "Клієнт"}
              </div>
              <div className="feature-deal-detail__timeline-meta">
                <span>
                  {STATUS_LABELS[viewing.status] || viewing.status || "—"}
                </span>
                {viewing.scheduled_at ? (
                  <span>
                    · <time dateTime={viewing.scheduled_at}>
                      {formatDateTimeUk(viewing.scheduled_at)}
                    </time>
                  </span>
                ) : null}
                {agentLabel(viewing.agent) ? (
                  <span>· {agentLabel(viewing.agent)}</span>
                ) : null}
              </div>
              {viewing.can_unlink === true ? (
                <>
                  <button
                    type="button"
                    className="action-btn"
                    disabled={unlinkState === "submitting"}
                    onClick={() => handleUnlinkRequest(viewing.id)}
                    data-testid={`deal-unlink-viewing-${viewing.id}`}
                    aria-expanded={unlinkConfirmId === viewing.id}
                  >
                    Відв&apos;язати
                  </button>
                  {unlinkConfirmId === viewing.id ? (
                    <div
                      className="feature-deal-detail__status-confirm"
                      role="alertdialog"
                      aria-label="Підтвердження відв'язування показу"
                      data-testid={`deal-unlink-viewing-confirm-${viewing.id}`}
                    >
                      <p>
                        Відв&apos;язати цей показ від угоди? Сам показ не буде
                        видалено.
                      </p>
                      <div className="feature-deal-detail__status-confirm-actions">
                        <button
                          type="button"
                          className="action-btn"
                          onClick={handleCancelUnlink}
                          disabled={unlinkState === "submitting"}
                        >
                          Скасувати
                        </button>
                        <button
                          type="button"
                          className="action-btn action-btn--danger"
                          onClick={() => void handleConfirmUnlink()}
                          disabled={unlinkState === "submitting"}
                          data-testid={`deal-unlink-viewing-confirm-action-${viewing.id}`}
                        >
                          {unlinkState === "submitting"
                            ? "Відв'язування…"
                            : "Відв'язати"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <DealLinkViewingPicker
        open={pickerOpen}
        dealId={dealId}
        candidates={candidates}
        loadState={candLoadState}
        loadError={candLoadError}
        onRetry={handleCandRetry}
        onClose={() => setPickerOpen(false)}
        onLink={handleLink}
        submitting={linking}
        formError={linkFormError}
        fieldErrors={linkFieldErrors}
      />
    </section>
  );
}

export default DealPrivateViewingsSection;
